/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { ref, onValue } from "firebase/database";
import { database } from "../../../config/firebaseConfig";
import axios from "../../../config/axios";
import useGlobalStore from "../../../store/global.store";
import { useTV } from "../../../hooks/useTV";
import { useRemote } from "../../../hooks/useRemote";
import { useMusicStreams } from "../../../hooks/useMusicStreams";
import LiveMusicPlayer from "../LiveMusicPlayer/LiveMusicPlayer";
import { IoSettingsOutline } from "react-icons/io5";
import { LuVolume1, LuVolume2, LuVolumeOff } from "react-icons/lu";

const albumId = "default";


// ── Layout constants — EXPLICIT per resolution ────────────────────────────────
// DISPLAY = 1280x800, TV = 1920x1080. No fluid clamp() sizing; every value is
// fixed per mode, same philosophy as Dashboard's HEADER_H_DISPLAY / HEADER_H_TV.
const SIZES = {
  display: {
    tickerH: 67,
    topBarH: 78,
    tickerFont: 26,
    tibTime: 44,
    tibAmPm: 16,
    tibDate: 22,
    tibDays: 24,
    ltCaption: 34,
    ltDate: 18,
    plCaption: 22,
    plDate: 16,
    emptyTitle: 34,
    emptySub: 22,
    countPill: 12,
    exitFont: 20,
    exitPad: "8px 22px",
    exitBorder: 3,
    barPadX: 48,
    cdDays: 32,
    cdLabel: 16,
    cdGap: 1,
    qrEmpty: 150,
    qrModal: 260,
    plusBtn: 74,
    stepFont: 20,
  },
  tv: {
    tickerH: 90,
    topBarH: 105,
    tickerFont: 36,
    tibTime: 60,
    tibAmPm: 22,
    tibDate: 30,
    tibDays: 32,
    ltCaption: 48,
    ltDate: 26,
    plCaption: 30,
    plDate: 22,
    emptyTitle: 46,
    emptySub: 30,
    countPill: 16,
    exitFont: 30,
    exitPad: "10px 30px",
    exitBorder: 5,
    barPadX: 64,
    cdDays: 46,
    cdLabel: 22,
    cdGap: 2,
    qrEmpty: 200,
    qrModal: 340,
    plusBtn: 96,
    stepFont: 28,
  },
};

const PARK_ORDER = ["MK", "EPCOT", "HS", "AK", "DL", "CA"];

const TICKER_THEMES = {
  MK: { bg: "#0b2e59", text: "#f4c76a" },
  EPCOT: { bg: "#939394", text: "#ffffff" },
  HS: { bg: "#1fa3a8", text: "#ffffff" },
  AK: { bg: "#4b3a2f", text: "#6fbf73" },
  DL: { bg: "#d94a7c", text: "#ffffff" },
  CA: { bg: "#ffb300", text: "#f30606" },
};

// Ignore nowPlaying entries older than this (fixes reload re-pin).
const NOW_PLAYING_MAX_AGE_MS = 30 * 1000;

// Always use EST regardless of device system time
function getNowEST() {
  return new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return -1;
  const now = getNowEST();
  now.setHours(0, 0, 0, 0);
  const trip = new Date(dateStr);
  if (isNaN(trip.getTime())) return -1;
  trip.setHours(0, 0, 0, 0);
  return Math.ceil((trip - now) / (1000 * 60 * 60 * 24));
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Same schema logic as WaitTimesScroll — reads the ride synchronizer's RTDB shape.
function formatWait(ride) {
  if (!ride) return "\u2014";
  const status = ride.status;
  if (status === "OPERATING") {
    if (ride.resp_code === 0 && typeof ride.wait_time === "number") {
      return `${ride.wait_time} min`;
    }
    if (ride.resp_msg === "Virtual Queue") return "VQ";
    return "Open";
  }
  if (status === "CLOSED" || status === "DOWN" || status === "REFURBISHMENT") {
    return "Closed";
  }
  return status || "\u2014";
}

function rideNum(id) {
  const n = parseInt(String(id).split("_")[1], 10);
  return Number.isFinite(n) ? n : 0;
}

const makeStyles = (S) => `
  .pf-root, .pf-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .pf-root {
    width: 100vw; height: 100vh; background: #000;
    overflow: hidden; position: fixed; inset: 0; z-index: 105;
    cursor: pointer; user-select: none;
    font-family: 'Lato', sans-serif;
  }

  .pf-root .slide {
    position: absolute;
    top: var(--top-offset, 0px);
    bottom: var(--bottom-offset, 0px);
    left: 0; right: 0;
    will-change: transform, opacity, filter;
  }

  .pf-root .fade-idle  { opacity: 1; }
  .pf-root .fade-exit  { opacity: 0; transition: opacity var(--dur) ease; }
  .pf-root .fade-enter { opacity: 0; }
  .pf-root .fade-in    { opacity: 1; transition: opacity var(--dur) ease; }
  .pf-root .slide-left-idle  { transform: translateX(0); }
  .pf-root .slide-left-exit  { transform: translateX(-100%); transition: transform var(--dur) cubic-bezier(0.76,0,0.24,1); }
  .pf-root .slide-left-enter { transform: translateX(100%); }
  .pf-root .slide-left-in    { transform: translateX(0); transition: transform var(--dur) cubic-bezier(0.76,0,0.24,1); }
  .pf-root .slide-right-idle  { transform: translateX(0); }
  .pf-root .slide-right-exit  { transform: translateX(100%); transition: transform var(--dur) cubic-bezier(0.76,0,0.24,1); }
  .pf-root .slide-right-enter { transform: translateX(-100%); }
  .pf-root .slide-right-in    { transform: translateX(0); transition: transform var(--dur) cubic-bezier(0.76,0,0.24,1); }
  .pf-root .zoom-in-idle  { opacity: 1; transform: scale(1); }
  .pf-root .zoom-in-exit  { opacity: 0; transform: scale(0.92); transition: opacity var(--dur) ease, transform var(--dur) ease; }
  .pf-root .zoom-in-enter { opacity: 0; transform: scale(1.12); }
  .pf-root .zoom-in-in    { opacity: 1; transform: scale(1); transition: opacity var(--dur) ease, transform var(--dur) ease; }
  .pf-root .zoom-out-idle  { opacity: 1; transform: scale(1); }
  .pf-root .zoom-out-exit  { opacity: 0; transform: scale(1.1); transition: opacity var(--dur) ease, transform var(--dur) ease; }
  .pf-root .zoom-out-enter { opacity: 0; transform: scale(0.88); }
  .pf-root .zoom-out-in    { opacity: 1; transform: scale(1); transition: opacity var(--dur) ease, transform var(--dur) ease; }
  .pf-root .fade-blur-idle  { opacity: 1; filter: blur(0px); }
  .pf-root .fade-blur-exit  { opacity: 0; filter: blur(20px); transition: opacity var(--dur) ease, filter var(--dur) ease; }
  .pf-root .fade-blur-enter { opacity: 0; filter: blur(20px); }
  .pf-root .fade-blur-in    { opacity: 1; filter: blur(0px); transition: opacity var(--dur) ease, filter var(--dur) ease; }
  .pf-root .cross-zoom-idle  { opacity: 1; transform: scale(1); }
  .pf-root .cross-zoom-exit  { opacity: 0; transform: scale(0.96); transition: opacity var(--dur) ease, transform var(--dur) ease; }
  .pf-root .cross-zoom-enter { opacity: 0; transform: scale(1.08); }
  .pf-root .cross-zoom-in    { opacity: 1; transform: scale(1); transition: opacity var(--dur) ease, transform var(--dur) ease; }
  .pf-root .kenburns-idle  { opacity: 1; animation: pf-kb var(--interval) ease forwards; }
  .pf-root .kenburns-exit  { opacity: 0; transition: opacity var(--dur) ease; }
  .pf-root .kenburns-enter { opacity: 0; }
  .pf-root .kenburns-in    { opacity: 1; transition: opacity var(--dur) ease; animation: pf-kb var(--interval) ease forwards; }
  @keyframes pf-kb { from{transform:scale(1)} to{transform:scale(1.06) translate(-1%,-1%)} }

  .pf-root .landscape-wrap {
    position: relative; width: 100%; height: 100%; overflow: hidden; background: #1a0e00;
  }
  .pf-root .landscape-bg {
    position: absolute; inset: -10%;
    background-size: cover; background-position: center;
    filter: blur(48px) brightness(0.40) saturate(1.3) sepia(0.2);
    transform: scale(1.15);
  }
  .pf-root .landscape-img {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: contain; z-index: 1;
  }

  .pf-root .vignette {
    position: absolute; inset: 0; z-index: 2; pointer-events: none;
    background: radial-gradient(ellipse at center,
      transparent 48%,
      rgba(0,0,0,0.18) 70%,
      rgba(0,0,0,0.52) 100%
    );
  }

  .pf-root .portrait-wrap {
    position: relative; width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center; overflow: hidden;
    background: #1a0e00;
  }
  .pf-root .portrait-bg {
    position: absolute; inset: -10%;
    background-size: cover; background-position: center;
    filter: blur(48px) brightness(0.35) saturate(1.3) sepia(0.2);
    transform: scale(1.15);
  }
  .pf-root .portrait-bg-tint { position: absolute; inset: 0; background: rgba(0,0,0,0.12); }
  .pf-root .portrait-pair {
    position: relative; z-index: 1; display: flex; gap: 0;
    align-items: stretch; justify-content: space-between;
    padding: 0; height: 100%; width: 100%;
  }
  .pf-root .portrait-pair .portrait-slot:first-child {
    border-right: 2px solid rgba(201,162,39,0.20);
  }
  .pf-root .portrait-solo .portrait-slot { max-width: 100%; flex: 1; }
  .pf-root .portrait-slot {
    flex: 1; height: 100%; position: relative;
    display: flex; align-items: center; justify-content: center;
    overflow: hidden;
  }
  .pf-root .portrait-img {
    height: 100%; width: auto;
    object-fit: contain;
    display: block;
  }

  .pf-root .lower-third {
    position: absolute; bottom: 0; left: 0; right: 0; z-index: 5;
    background: linear-gradient(
      to top,
      rgba(10, 4, 0, 0.88) 0%,
      rgba(10, 4, 0, 0.60) 40%,
      transparent 100%
    );
    padding: 48px 48px 28px;
    display: flex; flex-direction: column; gap: 6px;
  }
  .pf-root .lt-caption {
    font-size: ${S.ltCaption}px;
    font-weight: 300;
    font-family: 'Lato', sans-serif;
    color: rgba(255,248,235,0.97);
    letter-spacing: 0.2px; line-height: 1.2;
    text-shadow: 0 2px 16px rgba(0,0,0,0.5);
  }
  .pf-root .lt-date {
    font-size: ${S.ltDate}px;
    font-weight: 400; color: rgba(255,235,180,0.60); letter-spacing: 0.5px;
    margin-top: 2px;
  }

  .pf-root .portrait-lower {
    position: absolute; bottom: 0; left: 0; right: 0;
    background: linear-gradient(
      to top,
      rgba(10, 4, 0, 0.90) 0%,
      rgba(10, 4, 0, 0.55) 50%,
      transparent 100%
    );
    padding: 36px 16px 18px;
    display: flex; flex-direction: column; gap: 4px;
  }
  .pf-root .portrait-lt-caption {
    font-size: ${S.plCaption}px;
    font-weight: 300;
    font-family: 'Lato', sans-serif;
    color: rgba(255,248,235,0.97); line-height: 1.25;
    text-shadow: 0 2px 12px rgba(0,0,0,0.5);
  }
  .pf-root .portrait-lt-date {
    font-size: ${S.plDate}px;
    color: rgba(255,235,180,0.58); letter-spacing: 0.3px;
  }

  .pf-root .busy-hint {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    z-index: 35; pointer-events: none;
    width: 44px; height: 44px; border-radius: 50%;
    border: 3px solid rgba(255,255,255,0.25);
    border-top-color: rgba(255,255,255,0.9);
    animation: pf-spin 0.8s linear infinite;
  }
  @keyframes pf-spin { to { transform: translate(-50%, -50%) rotate(360deg); } }

  .pf-root .tap-flash {
    position: absolute; inset: 0; z-index: 30;
    background: rgba(255,255,255,0.06);
    animation: pf-tapFlash 0.35s ease forwards; pointer-events: none;
  }
  @keyframes pf-tapFlash { from{opacity:1} to{opacity:0} }

  .pf-root .top-info-bar {
    position: absolute; left: 0; right: 0; top: 0; z-index: 10;
    height: ${S.topBarH}px;
    background: linear-gradient(135deg, #003580 0%, #0055cc 60%, #007bff 100%);
    padding: 0 ${S.barPadX}px;
    display: flex; align-items: center; justify-content: flex-start;
    box-shadow: 0 4px 24px rgba(0,53,128,0.45);
    pointer-events: none;
  }
  /* Centered countdown hero — absolutely centered on the whole bar so the
     left clock/date cluster never shifts it off-center. */
  .pf-root .tib-countdown {
    position: absolute; left: 50%; top: 50%;
    transform: translate(-50%, -50%);
    display: flex; flex-direction: column; align-items: center;
    line-height: 1.05; text-align: center; white-space: nowrap;
  }
  /* Display: countdown lives inline in the left cluster (after a divider),
     left-aligned — not absolutely centered. */
  .pf-root .tib-countdown.tib-cd-inline {
    position: static; left: auto; top: auto; transform: none;
    align-items: flex-start; text-align: left;
  }
  .pf-root .tib-cd-days {
    font-family: 'Lato', sans-serif; font-weight: 800;
    font-size: ${S.cdDays}px; letter-spacing: 0.5px;
    background: linear-gradient(
      100deg,
      #ffe9a8 0%, #f4c76a 20%, #ffffff 46%, #fff6d8 52%,
      #f4c76a 78%, #ffe9a8 100%
    );
    background-size: 220% auto;
    -webkit-background-clip: text; background-clip: text;
    -webkit-text-fill-color: transparent; color: transparent;
    animation: pf-cdShimmer 5.5s linear infinite;
    text-shadow: 0 2px 14px rgba(0,0,0,0.20);
  }
  .pf-root .tib-cd-label {
    font-family: 'Lato', sans-serif; font-weight: 500;
    font-size: ${S.cdLabel}px; letter-spacing: 1.5px;
    color: rgba(255,255,255,0.92); text-transform: uppercase;
    margin-top: ${S.cdGap}px;
  }
  @keyframes pf-cdShimmer {
    0%   { background-position: 200% center; }
    100% { background-position: -20% center; }
  }
  .pf-root .tib-cd-today {
    font-family: 'Lato', sans-serif; font-weight: 800;
    font-size: ${S.cdDays}px; letter-spacing: 0.5px;
    background: linear-gradient(
      100deg,
      #ffe9a8 0%, #f4c76a 20%, #ffffff 46%, #fff6d8 52%,
      #f4c76a 78%, #ffe9a8 100%
    );
    background-size: 220% auto;
    -webkit-background-clip: text; background-clip: text;
    -webkit-text-fill-color: transparent; color: transparent;
    animation: pf-cdShimmer 5.5s linear infinite;
  }
  .pf-root .tib-left { display: flex; flex-direction: row; align-items: baseline; gap: 18px; }
  .pf-root .tib-left:has(.tib-cd-inline) { align-items: flex-end; }
  .pf-root .tib-cd-inline .tib-cd-label { padding-bottom: 1px; }
  .pf-root .tib-cd-inline + *, .pf-root .tib-divider { align-self: center; }
  .pf-root .tib-time {
    font-size: ${S.tibTime}px;
    font-weight: 400; color: rgba(255,255,255,1.0);
    letter-spacing: -2px; line-height: 1;
  }
  .pf-root .tib-ampm {
    font-size: ${S.tibAmPm}px; font-weight: 300;
    margin-left: 5px; color: rgba(255,255,255,1.0); vertical-align: super;
  }
  .pf-root .tib-date {
    font-size: ${S.tibDate}px; font-weight: 400;
    color: rgba(255,255,255,0.92); letter-spacing: 0.3px;
  }
  .pf-root .tib-divider { width: 1px; height: 60%; background: rgba(255,255,255,0.35); align-self: center; }
  .pf-root .tib-days-single {
    font-size: ${S.tibDays}px;
    font-weight: 500; color: rgba(255,255,255,1.0);
    letter-spacing: 0.4px; white-space: nowrap;
  }
  .pf-root .tib-today {
    font-size: ${S.tibDays}px;
    font-weight: 300; color: rgba(255,248,235,0.96);
    white-space: nowrap;
  }

  .pf-root .progress-outer {
    position: absolute; bottom: 0; left: 0; right: 0; pointer-events: none;
  }
  .pf-root .progress-inner {
    display: flex; gap: 4px; align-items: center; padding: 0 ${S.barPadX}px 10px;
  }
  .pf-root .progress-track { flex:1; height:2px; background:rgba(255,255,255,0.12); border-radius:1px; overflow:hidden; }
  .pf-root .progress-fill { height:100%; width:100%; transform-origin:left center; border-radius:1px; transform:scaleX(0); }
  .pf-root .progress-fill.done    { transform:scaleX(1); background:rgba(201,162,39,0.55); }
  .pf-root .progress-fill.active  { transform:scaleX(1); background:rgba(201,162,39,0.9);
    transition:transform linear; transition-duration:var(--interval); }
  .pf-root .progress-fill.pending { transform:scaleX(0); background:rgba(255,255,255,0.12); }

  .pf-root .offline-dot {
    position: absolute; left: 40px;
    display: flex; align-items: center; gap: 5px;
    background: rgba(0,0,0,0.55); backdrop-filter: blur(8px);
    border-radius: 100px; padding: 5px 10px; pointer-events: none;
  }
  .pf-root .offline-dot-circle { width:6px; height:6px; border-radius:50%; background:#ef4444; }
  .pf-root .offline-dot-label  { font-size:11px; color:rgba(255,255,255,0.55); }

  .pf-root .count-pill {
    position: absolute; right: 20px;
    background: rgba(0,0,0,0.45); backdrop-filter: blur(8px);
    border-radius: 100px; padding: 5px 12px; pointer-events: none;
    font-size: ${S.countPill}px; color: rgba(255,255,255,0.55); letter-spacing: 0.3px;
  }

  .pf-root .pf-exit-btn {
    position: absolute; top: 16px; right: 16px; z-index: 50;
    background: rgba(220,38,38,0.85);
    color: #fff; font-family: 'Lato', sans-serif; font-weight: 700;
    border: ${S.exitBorder}px solid rgba(255,255,255,0.9);
    border-radius: 14px; padding: ${S.exitPad}; font-size: ${S.exitFont}px;
    cursor: pointer; letter-spacing: 1px; outline: none;
  }
  .pf-root .pf-exit-btn.tv-focused { border-color: #3ADF3C; }
  .pf-root .pf-add-btn-tv {
    position: absolute; right: 16px; z-index: 50;
    background: linear-gradient(135deg, #003580, #007bff);
    color: #fff; font-family: 'Lato', sans-serif; font-weight: 700;
    border: ${S.exitBorder}px solid rgba(255,255,255,0.9);
    border-radius: 14px; padding: ${S.exitPad}; font-size: ${S.exitFont}px;
    cursor: pointer; letter-spacing: 1px; outline: none;
  }
  .pf-root .pf-add-btn-tv.tv-focused { border-color: #3ADF3C; }
  .pf-root .pf-qr-close-tv {
    margin-top: 6px;
    background: rgba(220,38,38,0.85);
    color: #fff; font-family: 'Lato', sans-serif; font-weight: 700;
    border: ${S.exitBorder}px solid rgba(255,255,255,0.9);
    border-radius: 14px; padding: ${S.exitPad}; font-size: ${S.exitFont}px;
    cursor: pointer; letter-spacing: 1px; outline: none;
  }
  .pf-root .pf-qr-close-tv:focus { border-color: #3ADF3C; }
  .pf-root .pf-ctrl-btn {
    background: #4f5051; color: #fff;
    width: 64px; border-radius: 10px; cursor: pointer;
    border: 3px solid #fafbfa;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    display: flex; align-items: center; justify-content: center;
    -webkit-tap-highlight-color: transparent; user-select: none;
    outline: none; flex-shrink: 0;
  }
  .pf-root .pf-ctrl-btn.unmuted { border-color: #05f651; color: #05f651; }
  .pf-root .pf-ctrl-btn.muted { border-color: red; color: red; }
  .pf-root .pf-music-sel {
    width: auto; padding: 0 16px;
    font-family: 'Lato', sans-serif; font-weight: 700;
    font-size: 15px; letter-spacing: 0.4px; color: #fff;
    text-align: center; line-height: 1.15;
  }
  .pf-root .pf-topbar {
    position: absolute; top: ${S.exitFont >= 30 ? 16 : 8}px; right: 16px; z-index: 50;
    display: flex; gap: 10px; align-items: stretch;
  }
  .pf-root .pf-topbar .pf-exit-btn,
  .pf-root .pf-topbar .pf-ctrl-btn,
  .pf-root .pf-topbar .pf-add-btn-tv {
    position: static; top: auto; right: auto;
    display: flex; align-items: center; justify-content: center;
    height: ${Math.round(S.exitFont * (S.exitFont >= 30 ? 2.35 : 3.0))}px;
    padding: 0 ${S.exitFont >= 30 ? 22 : 16}px;
    text-align: center; line-height: 1.15;
  }

  .pf-root .sparkles {
    position: absolute; inset: 0; z-index: 3; pointer-events: none; overflow: hidden;
  }
  .pf-root .sparkle {
    position: absolute;
    width: 2px; height: 2px; border-radius: 50%;
    background: rgba(201,162,39,0.75);
    animation: pf-sparkleFade var(--sd) ease-in-out var(--sd2) infinite;
  }
  @keyframes pf-sparkleFade {
    0%   { opacity: 0; transform: scale(0) translateY(0); }
    30%  { opacity: 1; transform: scale(1.5) translateY(-8px); }
    100% { opacity: 0; transform: scale(0.5) translateY(-22px); }
  }

  .pf-root .pf-steps {
    display: flex; flex-direction: column; gap: 10px;
    text-align: left; margin-top: 6px;
  }
  .pf-root .pf-step {
    display: flex; align-items: center; gap: 14px;
    font-size: ${S.stepFont}px; color: rgba(255,255,255,0.85);
  }
  .pf-root .pf-step-num {
    display: flex; align-items: center; justify-content: center;
    width: ${S.stepFont + 14}px; height: ${S.stepFont + 14}px; border-radius: 50%;
    background: rgba(255,255,255,0.14); border: 1px solid rgba(255,255,255,0.35);
    font-weight: 700; font-size: ${S.stepFont - 4}px; flex-shrink: 0;
  }
  .pf-root .pf-qr-img {
    background: #fff; border-radius: 16px; padding: 10px;
  }
  .pf-root .pf-plus-btn {
    position: absolute; right: 20px; z-index: 40;
    width: ${S.plusBtn}px; height: ${S.plusBtn}px; border-radius: 50%;
    background: #ffffff; border: 3px solid #003580;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 12px rgba(0,30,80,0.25); cursor: pointer;
    transition: bottom 0.3s ease;
  }
  .pf-root .pf-plus-glyph {
    font-size: ${Math.round(S.plusBtn * 0.38)}px; color: #003580; font-weight: 300; line-height: 1;
  }
  .pf-root .pf-qr-overlay {
    position: absolute; inset: 0; z-index: 120;
    background: rgba(0,0,0,0.75); backdrop-filter: blur(10px);
    display: flex; align-items: center; justify-content: center;
  }
  .pf-root .pf-qr-card {
    position: relative;
    background: radial-gradient(circle at top, #0d3d8f 0%, #01023f 100%);
    border-radius: 24px; padding: 34px 40px;
    display: flex; flex-direction: column; align-items: center; gap: 18px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
  }
  .pf-root .pf-qr-close {
    position: absolute; top: 12px; right: 12px;
    width: 44px; height: 44px; border-radius: 50%;
    background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.3);
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; font-weight: 700; color: #fff; cursor: pointer;
  }
  .pf-root .pf-qr-title { color: #fff; font-size: ${S.emptyTitle - 6}px; font-weight: 600; }
  .pf-root .pf-qr-sub   { color: rgba(255,255,255,0.75); font-size: ${S.stepFont}px; }

  .pf-root .pf-loading {
    width: 100%; height: 100%;
    background: radial-gradient(circle at center, #0d3d8f 0%, #01023f 70%);
    display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 18px; position: relative;
  }
  .pf-root .pf-loading-title {
    color: #fff; font-size: ${S.emptyTitle}px; font-weight: 300;
    letter-spacing: 0.5px; animation: pf-loadPulse 1.8s ease-in-out infinite;
  }
  .pf-root .pf-loading-dots {
    display: flex; gap: 8px;
  }
  .pf-root .pf-loading-dots span {
    width: 9px; height: 9px; border-radius: 50%;
    background: rgba(201,162,39,0.85);
    animation: pf-loadDot 1.2s ease-in-out infinite;
  }
  .pf-root .pf-loading-dots span:nth-child(2) { animation-delay: 0.2s; }
  .pf-root .pf-loading-dots span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes pf-loadPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
  @keyframes pf-loadDot {
    0%,100% { transform: translateY(0); opacity: 0.5; }
    50% { transform: translateY(-8px); opacity: 1; }
  }

  .pf-root .pf-empty {
    width:100%; height:100%;
    background: radial-gradient(circle at center, #0d3d8f 0%, #01023f 70%);
    display:flex; align-items:center; justify-content:center;
    flex-direction:column; gap:16px; text-align:center; color:#fff;
  }
  .pf-root .pf-empty-title { font-size: ${S.emptyTitle}px; font-weight: 300; letter-spacing: 0.5px; }
  .pf-root .pf-empty-sub   { font-size: ${S.emptySub}px; color: rgba(255,255,255,0.65); }

  .pf-root .top-ticker {
    position: absolute; left: 0; right: 0; bottom: 0;
    height: ${S.tickerH}px;
    background-color: var(--ticker-bg, #0b2e59);
    border-top: 3px solid rgba(255,255,255,0.15);
    backdrop-filter: blur(10px);
    overflow: hidden;
    transition: background-color 0.6s ease;
    pointer-events: none;
    z-index: 10;
  }
  .pf-root .top-ticker-inner {
    position: absolute; inset: 0;
    display: flex; align-items: center;
    padding: 0 14px; overflow: hidden;
  }
  .pf-root .top-marquee {
    display: inline-flex; align-items: center;
    white-space: nowrap; will-change: transform;
    animation: pf-topTickerScroll linear infinite;
  }
  .pf-root .top-tick {
    display: inline-flex; align-items: center;
    font-family: 'Lato', sans-serif;
    font-size: ${S.tickerFont}px; font-weight: 500;
    color: var(--ticker-text, #f4c76a);
    letter-spacing: 0.2px; padding-right: 14px;
    transition: color 0.6s ease;
  }
  .pf-root .top-sep {
    display: inline-block; opacity: 0.70;
    padding-left: 14px; color: rgba(255,255,255,0.65);
  }
  @keyframes pf-topTickerScroll {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

function injectStyles(isTv) {
  const S = isTv ? SIZES.tv : SIZES.display;
  const css = makeStyles(S);
  let el = document.getElementById("photo-frame-styles");
  if (!el) {
    el = document.createElement("style");
    el.id = "photo-frame-styles";
    document.head.appendChild(el);
  }
  // CRITICAL: only write when changed. Reassigning textContent on every render
  // forces a full stylesheet reparse — for one frame the transition classes
  // don't apply and the incoming slide flashes fully visible.
  if (el.textContent !== css) el.textContent = css;
}

const SPARKLE_DEFS = [
  { left: "8%", top: "72%", sd: "4.2s", sd2: "0s" },
  { left: "15%", top: "30%", sd: "5.8s", sd2: "1.1s" },
  { left: "23%", top: "85%", sd: "3.9s", sd2: "2.3s" },
  { left: "38%", top: "18%", sd: "6.1s", sd2: "0.7s" },
  { left: "52%", top: "60%", sd: "4.7s", sd2: "3.1s" },
  { left: "61%", top: "88%", sd: "5.2s", sd2: "1.8s" },
  { left: "74%", top: "25%", sd: "3.7s", sd2: "0.4s" },
  { left: "82%", top: "55%", sd: "6.3s", sd2: "2.6s" },
  { left: "90%", top: "78%", sd: "4.5s", sd2: "1.5s" },
  { left: "46%", top: "42%", sd: "5.5s", sd2: "3.8s" },
];

function Sparkles() {
  return (
    <div className="sparkles">
      {SPARKLE_DEFS.map((sp, i) => (
        <div
          key={i}
          className="sparkle"
          style={{ left: sp.left, top: sp.top, "--sd": sp.sd, "--sd2": sp.sd2 }}
        />
      ))}
    </div>
  );
}

function buildGroups(photos) {
  const groups = [];
  let i = 0;
  while (i < photos.length) {
    const p = photos[i],
      next = photos[i + 1];
    if (p.isPortrait === true) {
      if (next?.isPortrait === true) {
        groups.push({ type: "portrait-pair", photos: [p, next] });
        i += 2;
      } else {
        groups.push({ type: "portrait-solo", photos: [p] });
        i += 1;
      }
    } else {
      groups.push({ type: "landscape", photos: [p] });
      i++;
    }
  }
  return groups.length ? groups : [{ type: "landscape", photos }];
}

// Stable identity for a photo group — used as React key so a finished
// incoming slide BECOMES the base slide (same DOM node) instead of the base
// slide's <img> src being mutated in place. An in-place src swap keeps
// painting the OLD image for a frame or two, which is exactly the
// photo-area flash / "jumps back to the prior photo" artifact.
function groupKey(g) {
  return ((g?.photos || []).map((p) => p.id).join("|") || "empty") + ":" + (g?.type || "");
}

function preloadGroup(g) {
  g?.photos?.forEach((p) => {
    if (p.url) {
      const img = new Image();
      img.src = p.url;
    }
  });
}

// Resolves when every image in the group is fetched AND decoded, so the
// incoming slide can paint fully on its first frame (no blank pop-in).
// Hard 1500ms cap so a slow image can never stall the slideshow.
function preloadAndDecode(g) {
  const jobs = (g?.photos || []).map(
    (p) =>
      new Promise((resolve) => {
        if (!p.url) return resolve();
        const img = new Image();
        const done = () => resolve();
        img.src = p.url;
        if (img.decode) {
          img.decode().then(done).catch(done);
        } else {
          img.onload = done;
          img.onerror = done;
        }
        setTimeout(done, 1500);
      }),
  );
  return Promise.all(jobs);
}

// ── Ride Wait Ticker — RTDB-fed (waitTimes/parks), filtered by subscribed parks
function WaitTimeTicker({ visible, subscribedParkKeys }) {
  const [parksData, setParksData] = useState({});
  const [activePark, setActivePark] = useState("MK");
  const marqueeRef = useRef(null);
  const itemRefs = useRef([]);
  const [cumWidths, setCumWidths] = useState([]);
  const [totalWidth, setTotalWidth] = useState(0);

  // One RTDB subscription — wait data is identical for every device.
  useEffect(() => {
    if (!visible) return;
    const r = ref(database, "waitTimes/parks");
    const unsubscribe = onValue(r, (snap) => {
      setParksData(snap.exists() ? snap.val() || {} : {});
    });
    return () => unsubscribe();
  }, [visible]);

  const shownParks = useMemo(() => {
    if (Array.isArray(subscribedParkKeys) && subscribedParkKeys.length) {
      return PARK_ORDER.filter((pk) => subscribedParkKeys.includes(pk));
    }
    return PARK_ORDER;
  }, [subscribedParkKeys]);

  const items = useMemo(() => {
    const rows = [];
    for (const pk of shownParks) {
      const parkObj = parksData?.[pk];
      if (!parkObj || typeof parkObj !== "object") continue;
      const ids = Object.keys(parkObj).sort((a, b) => rideNum(a) - rideNum(b));
      for (const id of ids) {
        const rideData = parkObj[id];
        if (!rideData) continue;
        rows.push({
          parkCode: pk,
          name: rideData.name || id,
          wait: formatWait(rideData),
        });
      }
    }
    return rows;
  }, [parksData, shownParks]);

  const doubled = useMemo(() => [...items, ...items], [items]);

  useLayoutEffect(() => {
    if (!visible) return;
    itemRefs.current = itemRefs.current.slice(0, items.length);
    requestAnimationFrame(() => {
      const widths = [];
      for (let i = 0; i < items.length; i++) {
        const el = itemRefs.current[i];
        widths.push(el ? el.offsetWidth : 0);
      }
      const cum = [];
      let running = 0;
      for (const w of widths) {
        running += w;
        cum.push(running);
      }
      setCumWidths(cum);
      setTotalWidth(running);
    });
  }, [items.length, visible]);

  useEffect(() => {
    if (!marqueeRef.current || !totalWidth || !cumWidths.length) return;
    const el = marqueeRef.current;
    const tick = () => {
      const style = getComputedStyle(el);
      const transform = style.transform;
      if (!transform || transform === "none") return;
      const matrix = new DOMMatrixReadOnly(transform);
      const x = Math.abs(matrix.m41 || 0);
      const pos = totalWidth > 0 ? x % totalWidth : 0;
      let idx = 0;
      while (idx < cumWidths.length && pos >= cumWidths[idx]) idx++;
      const item = items[Math.min(idx, items.length - 1)];
      if (item) setActivePark(item.parkCode);
    };
    const interval = setInterval(tick, 250);
    return () => clearInterval(interval);
  }, [totalWidth, cumWidths, items]);

  const theme = TICKER_THEMES[activePark] || TICKER_THEMES.MK;
  const animDuration = Math.max(40, items.length * 4);

  if (!visible || items.length === 0) return null;

  return (
    <div
      className="top-ticker"
      style={{ "--ticker-bg": theme.bg, "--ticker-text": theme.text }}
    >
      <div className="top-ticker-inner">
        <div
          className="top-marquee"
          ref={marqueeRef}
          style={{ animationDuration: `${animDuration}s` }}
        >
          {doubled.map((item, idx) => {
            const isFirst = idx < items.length;
            return (
              <span
                key={`${item.parkCode}-${item.name}-${idx}`}
                className="top-tick"
                ref={(node) => {
                  if (isFirst) itemRefs.current[idx] = node;
                }}
              >
                {item.name}: {item.wait}
                <span className="top-sep">•</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Top Info Bar — user-timezone clock + date left, countdown right ──────────
function TopBar({ countDownDate, timezone, isTv }) {
  const tz = timezone || "America/New_York";
  const [now, setNow] = useState(moment().tz(tz));
  useEffect(() => {
    const t = setInterval(() => setNow(moment().tz(tz)), 1000);
    return () => clearInterval(t);
  }, [tz]);

  const timePart = now.format("h:mm");
  const ampm = now.format("A");
  const dateStr = now.format("dddd, MMMM D");

  const days = daysUntil(countDownDate);

  const countdown =
    days < 0 ? null : (
      <>
        <div className="tib-cd-days">
          {days} {days === 1 ? "Day" : "Days"}
        </div>
        <div className="tib-cd-label">Until the magic begins!</div>
      </>
    );
  // DISPLAY: clock → divider → countdown, all left-aligned (no date; it was
  // getting cramped). TV: clock + date left, countdown centered on the bar.
  if (!isTv) {
    return (
      <div className="top-info-bar">
        <div className="tib-left" style={{ alignItems: "flex-end" }}>
          <div className="tib-time">
            {timePart}
            <span className="tib-ampm">{ampm}</span>
          </div>
          {days >= 0 && (
            <>
              <div className="tib-divider" />
              <div className="tib-countdown tib-cd-inline">{countdown}</div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="top-info-bar">
      <div className="tib-left">
        <div className="tib-time">
          {timePart}
          <span className="tib-ampm">{ampm}</span>
        </div>
        <div className="tib-date">{dateStr}</div>
      </div>
      {days >= 0 && <div className="tib-countdown">{countdown}</div>}
    </div>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────────
function ProgressBar({ total, current, intervalSec, bottomOffset }) {
  const bottomPad = bottomOffset > 0 ? bottomOffset + 8 : 10;
  return (
    <div className="progress-outer">
      <div
        className="progress-inner"
        style={{ "--interval": `${intervalSec}s`, paddingBottom: bottomPad }}
      >
        {Array.from({ length: total }).map((_, i) => {
          const cls = i < current ? "done" : i === current ? "active" : "pending";
          return (
            <div key={i} className="progress-track">
              <div className={`progress-fill ${cls}`} key={`${i}-${current}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SlideContent({ group, slideKey }) {
  if (group.type === "landscape") {
    const p = group.photos[0];
    const caption = p.caption || null;
    const takenDate = p.takenAt
      ? new Date(p.takenAt).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;
    const hasBar = caption || takenDate;
    return (
      <div className="landscape-wrap">
        <div className="landscape-bg" style={{ backgroundImage: `url(${p.url})` }} />
        <img className="landscape-img" src={p.url} alt="" />
        <div className="vignette" />
        <Sparkles />
        {hasBar && (
          <div className="lower-third" key={slideKey}>
            {caption && <div className="lt-caption">{caption}</div>}
            {takenDate && <div className="lt-date">{takenDate}</div>}
          </div>
        )}
      </div>
    );
  }

  const solo = group.type === "portrait-solo";
  return (
    <div className="portrait-wrap">
      <div
        className="portrait-bg"
        style={{ backgroundImage: `url(${group.photos[0].url})` }}
      />
      <div className="portrait-bg-tint" />
      <Sparkles />
      <div className={`portrait-pair${solo ? " portrait-solo" : ""}`}>
        {group.photos.map((p, i) => {
          const caption = p.caption || null;
          const takenDate = p.takenAt
            ? new Date(p.takenAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : null;
          const hasBar = caption || takenDate;
          return (
            <div key={p.id || i} className="portrait-slot">
              <img className="portrait-img" src={p.url} alt="" />
              {hasBar && (
                <div className="portrait-lower" key={slideKey}>
                  {caption && <div className="portrait-lt-caption">{caption}</div>}
                  {takenDate && <div className="portrait-lt-date">{takenDate}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function showSolo({
  foundPhoto,
  currentRef,
  resumeIndexRef,
  busyRef,
  settingsRef,
  setNext,
  setCurrent,
  setPinnedPhoto,
  setProgressKey,
  restartTimer,
}) {
  const soloGroup = {
    type: foundPhoto.isPortrait === true ? "portrait-solo" : "landscape",
    photos: [foundPhoto],
  };
  if (typeof currentRef.current === "number")
    resumeIndexRef.current = currentRef.current;
  busyRef.current = true;
  const dur = Math.min(1100, settingsRef.current.intervalSec * 140);
  setNext({ index: "pinned", entering: false, soloGroup });
  requestAnimationFrame(() => {
    void document.documentElement.offsetHeight; // register enter state first
    requestAnimationFrame(() => {
      setNext({ index: "pinned", entering: true, soloGroup });
    });
  });
  setTimeout(() => {
    currentRef.current = "pinned";
    setCurrent("pinned");
    setPinnedPhoto(soloGroup);
    setNext(null);
    busyRef.current = false;
  }, dur + 80);
  restartTimer();
  setProgressKey((k) => k + 1);
}

// ── Main component ────────────────────────────────────────────────────────────
const PhotoFrame = ({ device, countDownDate, timezone }) => {
  const { isTv } = useTV();
  const S = isTv ? SIZES.tv : SIZES.display;
  injectStyles(isTv);
  const { updateView } = useRemote();
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);

  // userId: /users/device/:id populates `user`, so device.user is the full
  // user object; fall back to the raw id just in case.
  const userId = device?.user?._id || device?.user || null;

  const [photos, setPhotos] = useState([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);
  const [maxPhotos, setMaxPhotos] = useState(null);
  const [settings, setSettings] = useState({
    intervalSec: 8,
    transition: "slide-left",
    shuffle: false,
    showCountdown: true,
    showTicker: true,
    chronological: false,
  });
  const [subscribedParkKeys, setSubscribedParkKeys] = useState(null);
  const [current, setCurrent] = useState(0);
  const [next, setNext] = useState(null);
  const [tapFlash, setTapFlash] = useState(false);
  const [busyHint, setBusyHint] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const [pinnedPhoto, setPinnedPhoto] = useState(null);
  const [offline, setOffline] = useState(false);
  const [exitFocused, setExitFocused] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [addFocused, setAddFocused] = useState(false);
  const addButtonRef = useRef(null);
  const qrCloseRef = useRef(null);
  // Host-side gating only: the player component manages its own state;
  // we just need "is anything live" to show/hide the selector buttons.
  const { musicStreams } = useMusicStreams();
  const anyMusicLive = Object.values(musicStreams || {}).some(
    (s) => s?.videoId,
  );
  const musicPlayerRef = useRef(null);
  const [musicSound, setMusicSound] = useState({ volume: 50, isMuted: false });
  useEffect(() => {
    if (!anyMusicLive) return;
    const t = setTimeout(() => {
      const s = musicPlayerRef.current?.getSound?.();
      if (s) setMusicSound(s);
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyMusicLive]);

  const [musicPillFocused, setMusicPillFocused] = useState(false);
  const musicTopBtnRef = useRef(null);
  const [musicTopFocused, setMusicTopFocused] = useState(false);

  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const currentRef = useRef(0);
  const resumeIndexRef = useRef(0);
  const settingsRef = useRef(settings);
  const groupsRef = useRef([]);
  const prevPhotoIdsRef = useRef(new Set());
  const touchStartRef = useRef(null);
  const swipedRef = useRef(false);
  const shuffleOrderRef = useRef(null);
  const exitButtonRef = useRef(null);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Hide the global BottomBar while the frame is open (Dashboard pattern).
  useEffect(() => {
    setShowBottomBar(false);
    return () => setShowBottomBar(true);
  }, [setShowBottomBar]);

  // TV: when the QR modal opens, trap focus on its CLOSE button.
  useEffect(() => {
    if (isTv && showQR) {
      const t = setTimeout(() => qrCloseRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [isTv, showQR]);

  // TV: autofocus EXIT button so remote Select works immediately.
  useEffect(() => {
    if (!isTv) return;
    const t = setTimeout(() => {
      exitButtonRef.current?.focus();
    }, 100);
    return () => clearTimeout(t);
  }, [isTv]);

  const restartTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(
      advanceNext,
      settingsRef.current.intervalSec * 1000,
    );
  };

  const advanceTo = async (ni) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const dur = Math.min(1100, settingsRef.current.intervalSec * 140);
    const g = groupsRef.current;
    // Show a spinner only if the decode is slow enough to notice (>250ms),
    // so fast transitions don't flicker a spinner.
    const hintTimer = setTimeout(() => setBusyHint(true), 250);
    // Wait until the incoming images are fetched AND decoded — prevents the
    // blank-then-pop flash when a not-yet-decoded image fades in.
    if (typeof ni === "number" && g[ni]) await preloadAndDecode(g[ni]);
    clearTimeout(hintTimer);
    setBusyHint(false);
    setNext({ index: ni, entering: false });
    requestAnimationFrame(() => {
      // Force a synchronous layout flush so the browser has REGISTERED the
      // enter state (opacity 0) before we flip to the in state. Without this,
      // batching can commit both states in one paint and the slide appears
      // instantly at full opacity with no transition — the flash.
      void document.documentElement.offsetHeight;
      requestAnimationFrame(() => {
        setNext({ index: ni, entering: true });
      });
    });
    setTimeout(() => {
      currentRef.current = ni;
      setCurrent(ni);
      setPinnedPhoto(null);
      setNext(null);
      busyRef.current = false;
      if (typeof ni === "number") {
        const after = g[(ni + 1) % Math.max(g.length, 1)];
        if (after) preloadGroup(after);
      }
    }, dur + 80);
  };

  const advanceNext = () => {
    const g = groupsRef.current;
    if (!g.length) return;
    let nextIdx;
    if (currentRef.current === "pinned") {
      nextIdx = (resumeIndexRef.current + 1) % g.length;
    } else if (settingsRef.current.shuffle) {
      const cur = currentRef.current;
      if (
        !shuffleOrderRef.current ||
        shuffleOrderRef.current.length !== g.length
      ) {
        shuffleOrderRef.current = shuffleArray(
          Array.from({ length: g.length }, (_, i) => i),
        );
      }
      const pos = shuffleOrderRef.current.indexOf(cur);
      if (pos === -1 || pos === g.length - 1) {
        let o = shuffleArray(Array.from({ length: g.length }, (_, i) => i));
        while (o[0] === cur && g.length > 1) o = shuffleArray(o);
        shuffleOrderRef.current = o;
        nextIdx = o[0];
      } else {
        nextIdx = shuffleOrderRef.current[pos + 1];
      }
    } else {
      nextIdx = (currentRef.current + 1) % g.length;
    }
    advanceTo(nextIdx);
  };

  const advancePrev = () => {
    const g = groupsRef.current;
    if (!g.length) return;
    const ni =
      currentRef.current === "pinned"
        ? resumeIndexRef.current
        : (currentRef.current - 1 + g.length) % g.length;
    advanceTo(ni);
  };

  // ── Firebase: photos + settings + meta (READ-ONLY) ───────────────────────
  useEffect(() => {
    if (!userId) return;
    const r = ref(database, `users/${userId}/photoFrame/albums/${albumId}`);
    const unsubscribe = onValue(
      r,
      (snap) => {
        setOffline(false);
        setPhotosLoaded(true);
        const data = snap.val() || {};
        const arr = data.photos
          ? Object.entries(data.photos)
              .map(([id, val]) => ({ id, ...val }))
              .filter((p) => p?.url)
          : [];
        arr.sort((a, b) => (a.order || 0) - (b.order || 0));

        const newPhotos = arr.filter((p) => !prevPhotoIdsRef.current.has(p.id));
        const isFirstLoad = prevPhotoIdsRef.current.size === 0;
        prevPhotoIdsRef.current = new Set(arr.map((p) => p.id));

        setPhotos(arr);
        if (data.settings) setSettings((prev) => ({ ...prev, ...data.settings }));
        if (data.meta?.maxPhotos) setMaxPhotos(data.meta.maxPhotos);

        if (isFirstLoad) {
          preloadGroup(buildGroups(arr)[0]);
          preloadGroup(buildGroups(arr)[1]);
        }
        if (!isFirstLoad && newPhotos.length > 0) {
          setTimeout(() => {
            showSolo({
              foundPhoto: newPhotos[0],
              currentRef,
              resumeIndexRef,
              busyRef,
              settingsRef,
              setNext,
              setCurrent,
              setPinnedPhoto,
              setProgressKey,
              restartTimer,
            });
          }, 600);
        }
      },
      () => {
        setOffline(true);
        setPhotosLoaded(true);
      },
    );
    return () => unsubscribe();
  }, [userId]);

  // ── Firebase: connectivity dot ────────────────────────────────────────────
  useEffect(() => {
    const c = ref(database, ".info/connected");
    const unsubscribe = onValue(c, (s) => setOffline(!s.val()));
    return () => unsubscribe();
  }, []);

  // ── Subscribed parks for the ticker (Dashboard pattern) ──────────────────
  useEffect(() => {
    if (!device?._id) return;
    const fetchParks = () => {
      axios
        .get(`/parks/public/${device._id}`, { withCredentials: true })
        .then((res) => {
          setSubscribedParkKeys(res.data.parks.map((p) => p.park_code));
        })
        .catch(() => {});
    };
    fetchParks();
    const id = setInterval(fetchParks, 60 * 1000);
    return () => clearInterval(id);
  }, [device?._id]);

  // ── Photo limit auto-refresh ─────────────────────────────────────────────
  // Asks the server to recompute the account limit from the Chargebee-synced
  // subscription and rewrite meta.maxPhotos if it changed; the existing
  // onValue album subscription then applies the new limit live. Runs on
  // mount and every 5 minutes, so an add-on change made anywhere (Chargebee,
  // support) reaches the display within ~5 min — no remote visit required.
  useEffect(() => {
    if (!device?._id) return;
    const refreshLimit = () => {
      axios
        .get(`/photo-frame/refresh-limit/${device._id}`, { withCredentials: true })
        .catch(() => {});
    };
    refreshLimit();
    const id = setInterval(refreshLimit, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [device?._id]);

  // Account photo limit — mirrored into RTDB meta by the server. Fall back
  // to 200 if meta was never written (fail to base, matching the server).
  const photoLimit = maxPhotos || 200;

  // Active set = newest photoLimit photos by createdAt. After a downgrade a
  // user can have MORE stored than their current limit — stored count must
  // never be assumed to equal visible count, so slice by recency at read
  // time. Display order (order field / chronological) still applies WITHIN
  // the active set. createdAt is safe to rank by: it's set once at upload
  // confirm and never rewritten — unlike order, which /reorder repurposes
  // into a sort position.
  let activePhotos = photos;
  if (photos.length > photoLimit) {
    const newestIds = new Set(
      [...photos]
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        .slice(0, photoLimit)
        .map((p) => p.id),
    );
    activePhotos = photos.filter((p) => newestIds.has(p.id));
  }

  const sortedPhotos = settings.chronological
    ? [...activePhotos].sort((a, b) => {
        const aDate = a.takenAt ? new Date(a.takenAt) : new Date(0);
        const bDate = b.takenAt ? new Date(b.takenAt) : new Date(0);
        return aDate - bDate;
      })
    : activePhotos;
  const groups = buildGroups(sortedPhotos);
  groupsRef.current = groups;

  const durMs = Math.min(1100, settings.intervalSec * 140);
  const durSec = (durMs / 1000).toFixed(2);

  // ── Slideshow timer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!groups.length || !photos.length) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(
      advanceNext,
      settings.intervalSec * 1000,
    );
    return () => clearInterval(timerRef.current);
  }, [groups.length, settings.intervalSec, settings.shuffle, photos.length]);

  // ── Firebase: nowPlaying — with device-target + staleness guards ─────────
  useEffect(() => {
    if (!userId) return;
    const r = ref(
      database,
      `users/${userId}/photoFrame/albums/${albumId}/nowPlaying`,
    );
    const unsubscribe = onValue(r, (snap) => {
      const val = snap.val();
      if (!val?.photoId) return;

      // Guard 1: targeted at a different device → ignore.
      if (val.deviceId && val.deviceId !== device?._id) return;

      // Guard 2: stale entry (e.g. re-fired on frame reload) → ignore.
      if (!val.ts || Date.now() - val.ts > NOW_PLAYING_MAX_AGE_MS) return;

      let foundPhoto = null;
      for (const grp of groupsRef.current) {
        const match = grp.photos?.find((p) => p.id === val.photoId);
        if (match) {
          foundPhoto = match;
          break;
        }
      }
      if (!foundPhoto) return;
      showSolo({
        foundPhoto,
        currentRef,
        resumeIndexRef,
        busyRef,
        settingsRef,
        setNext,
        setCurrent,
        setPinnedPhoto,
        setProgressKey,
        restartTimer,
      });
    });
    return () => unsubscribe();
  }, [userId, device?._id]);

  // ── Touch / tap handlers ──────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartRef.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current;
    touchStartRef.current = null;
    if (Math.abs(dx) < 50) return;
    swipedRef.current = true;
    setTimeout(() => {
      swipedRef.current = false;
    }, 300);
    setTapFlash(true);
    setTimeout(() => setTapFlash(false), 350);
    restartTimer();
    if (dx < 0) advanceNext();
    else advancePrev();
  };

  const handleTap = () => {
    if (swipedRef.current) return;
    // Always flash on tap — immediate acknowledgment so it never feels frozen,
    // even if a transition is mid-flight (advanceNext no-ops while busy).
    setTapFlash(true);
    setTimeout(() => setTapFlash(false), 350);
    restartTimer();
    advanceNext();
  };

  const handleExit = (e) => {
    e.stopPropagation();
    updateView("Home");
  };

  const exitButton = (
    <button
      ref={exitButtonRef}
      className={`pf-exit-btn ${isTv && exitFocused ? "tv-focused" : ""}`}
      onClick={handleExit}
      onFocus={() => setExitFocused(true)}
      onBlur={() => setExitFocused(false)}
      onKeyDown={(e) => {
        if (!isTv) return;
        if (
          e.key === "ArrowUp" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight"
        ) {
          // Trap d-pad so focus stays on EXIT; left/right advance photos.
          e.preventDefault();
          e.stopPropagation();
          if (e.key === "ArrowRight") {
            restartTimer();
            advanceNext();
          }
          if (e.key === "ArrowLeft") {
            addButtonRef.current?.focus();
          }
          if (e.key === "ArrowDown") {
            musicPlayerRef.current?.focus();
          }
          return;
        }
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          updateView("Home");
        }
      }}
      tabIndex={0}
    >
      EXIT
    </button>
  );

  // ── Loading state: Firebase hasn't answered yet — don't flash the QR ──────
  if (!photosLoaded) {
    return (
      <div className="pf-root" style={{ cursor: "default" }}>
        <div className="pf-loading">
          <Sparkles />
          <div className="pf-loading-title">Loading your memories…</div>
          <div className="pf-loading-dots">
            <span />
            <span />
            <span />
          </div>
        </div>
        {exitButton}
      </div>
    );
  }

  // ── Empty state (only after Firebase confirmed there are no photos) ───────
  if (!photos.length) {
    return (
      <div className="pf-root" style={{ cursor: "default" }}>
        <div className="pf-empty">
          <div className="pf-empty-title">Let's Add Some Memories!</div>
          <img
            className="pf-qr-img"
            src="/qr.png"
            alt=""
            style={{ width: S.qrEmpty }}
          />
          <div className="pf-steps">
            <div className="pf-step">
              <span className="pf-step-num">1</span>
              <span>
                Scan the QR code or visit <b>waitsigns.com/remote</b>
              </span>
            </div>
            <div className="pf-step">
              <span className="pf-step-num">2</span>
              <span>Log in and tap the Photos tab</span>
            </div>
            <div className="pf-step">
              <span className="pf-step-num">3</span>
              <span>Add your photos & they'll appear here instantly!</span>
            </div>
          </div>
        </div>
        {exitButton}
      </div>
    );
  }

  const t = settings.transition;
  const showCD = settings.showCountdown !== false;
  const showTicker = settings.showTicker !== false;

  const topOffset = showCD ? S.topBarH : 0;
  const bottomOffset = showTicker ? S.tickerH : 0;

  const isT = next !== null;
  const entering = next?.entering ?? false;

  const currentGroup =
    current === "pinned" && pinnedPhoto ? pinnedPhoto : groups[current] ?? groups[0];
  const nextGroup =
    next?.index === "pinned" && next?.soloGroup
      ? next.soloGroup
      : groups[next?.index] ?? groups[0];

  return (
    <div
      className="pf-root"
      style={{
        "--dur": `${durSec}s`,
        "--interval": `${settings.intervalSec}s`,
        "--top-offset": `${topOffset}px`,
        "--bottom-offset": `${bottomOffset}px`,
      }}
      onClick={handleTap}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides — keyed array so React migrates the finished incoming slide
          into the base slot (same DOM node, no <img> src mutation). Order in
          the array = paint order: incoming renders after base, so it sits on
          top during the transition. */}
      {[
        <div
          key={`g-${groupKey(currentGroup)}`}
          className={`slide ${t}-${isT ? "exit" : "idle"}`}
        >
          <SlideContent group={currentGroup} slideKey={`c-${current}`} />
        </div>,
        isT && groupKey(nextGroup) !== groupKey(currentGroup) ? (
          <div
            key={`g-${groupKey(nextGroup)}`}
            className={`slide ${t}-${entering ? "in" : "enter"}`}
          >
            <SlideContent group={nextGroup} slideKey={`n-${String(next?.index)}`} />
          </div>
        ) : null,
      ]}

      {tapFlash && <div className="tap-flash" />}
      {busyHint && <div className="busy-hint" />}

      {/* Top bar (clock + countdown) */}
      {showCD && <TopBar countDownDate={countDownDate} timezone={timezone} isTv={isTv} />}

      {/* Bottom ticker (RTDB waitTimes) */}
      <WaitTimeTicker visible={showTicker} subscribedParkKeys={subscribedParkKeys} />

      {/* Offline indicator */}
      {offline && (
        <div
          className="offline-dot"
          style={{ zIndex: 31, bottom: bottomOffset > 0 ? bottomOffset + 8 : 16 }}
        >
          <div className="offline-dot-circle" />
          <span className="offline-dot-label">Offline</span>
        </div>
      )}

      {/* Live music player (shared component; hides itself when nothing live) */}
      <LiveMusicPlayer
        ref={musicPlayerRef}
        variant="floating"
        bottom={(bottomOffset > 0 ? bottomOffset : 6) + 10}
        right={20}
        upRef={exitButtonRef}
        pickerReturnRef={isTv ? musicTopBtnRef : undefined}
      />


      {/* Add-photos: circle on DISPLAY; EXIT-matched button on TV */}
      {!isTv && (
        <div
          className="pf-plus-btn"
          style={{ bottom: bottomOffset > 0 ? bottomOffset + 48 : 54, right: "auto", left: 20 }}
          onClick={(e) => {
            e.stopPropagation();
            setShowQR(true);
          }}
        >
          <div className="pf-plus-glyph">+</div>
        </div>
      )}
      {isTv && (
        <div className="pf-topbar">
          {anyMusicLive && (
          <button
            ref={musicTopBtnRef}
            className={`pf-add-btn-tv ${musicTopFocused ? "tv-focused" : ""}`}
            style={{ fontSize: S.exitFont * 0.75, maxWidth: S.exitFont * 9 }}
            onClick={(e) => {
              e.stopPropagation();
              musicPlayerRef.current?.switchRandom?.();
            }}
            onFocus={() => setMusicTopFocused(true)}
            onBlur={() => setMusicTopFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                e.preventDefault();
                e.stopPropagation();
                addButtonRef.current?.focus();
                return;
              }
              if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                musicPlayerRef.current?.focus();
                return;
              }
              if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                musicPlayerRef.current?.switchRandom?.();
              }
            }}
            tabIndex={0}
          >
            Change Music Streamer
          </button>
          )}
          <button
            ref={addButtonRef}
            className={`pf-add-btn-tv ${addFocused ? "tv-focused" : ""}`}
            style={{ fontSize: S.exitFont * 1.25, lineHeight: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              setShowQR(true);
            }}
            onFocus={() => setAddFocused(true)}
            onBlur={() => setAddFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                e.preventDefault();
                e.stopPropagation();
                exitButtonRef.current?.focus();
                return;
              }
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                e.stopPropagation();
                musicTopBtnRef.current?.focus();
                return;
              }
              if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                musicPlayerRef.current?.focus();
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                setShowQR((v) => !v);
              }
            }}
            tabIndex={0}
          >
            +
          </button>
          {exitButton}
        </div>
      )}
      {!isTv && (
        <div className="pf-topbar">
          {anyMusicLive && (
            <>
              <button
                className={`pf-ctrl-btn ${musicSound.isMuted ? "muted" : "unmuted"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const s = musicPlayerRef.current?.toggleMute();
                  if (s) setMusicSound(s);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                tabIndex={-1}
              >
                <LuVolumeOff size={34} />
              </button>
              <button
                className="pf-ctrl-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  const s = musicPlayerRef.current?.volumeUp();
                  if (s) setMusicSound(s);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                tabIndex={-1}
              >
                <LuVolume2 size={34} />
              </button>
              <button
                className="pf-ctrl-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  const s = musicPlayerRef.current?.volumeDown();
                  if (s) setMusicSound(s);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                tabIndex={-1}
              >
                <LuVolume1 size={34} />
              </button>
              <button
                className="pf-ctrl-btn pf-music-sel"
                onClick={(e) => {
                  e.stopPropagation();
                  musicPlayerRef.current?.switchRandom?.();
                }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                tabIndex={-1}
              >
                Change Music
                <br />
                Streamer
              </button>
            </>
          )}
          {exitButton}
        </div>
      )}

      {/* QR modal */}
      {showQR && (
        <div
          className="pf-qr-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setShowQR(false);
          }}
        >
          <div className="pf-qr-card" onClick={(e) => e.stopPropagation()}>
            {!isTv && (
              <div className="pf-qr-close" onClick={() => setShowQR(false)}>
                ✕
              </div>
            )}
            <div className="pf-qr-title">Want to add photos?</div>
            <img
              className="pf-qr-img"
              src="/qr.png"
              alt=""
              style={{ width: S.qrModal }}
            />
            <div className="pf-qr-sub">
              Scan the code or visit waitsigns.com/remote
            </div>
            {isTv && (
              <button
                ref={qrCloseRef}
                className="pf-qr-close-tv"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowQR(false);
                  addButtonRef.current?.focus();
                }}
                onKeyDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.key === "Enter" || e.key === " ") {
                    setShowQR(false);
                    addButtonRef.current?.focus();
                  }
                }}
                tabIndex={0}
              >
                CLOSE
              </button>
            )}
          </div>
        </div>
      )}


    </div>
  );
};

export default PhotoFrame;
